// "Right now": the home-page status board — what I'm working on, the last
// session off my watch, training form, tennis record and the latest journal
// note, all read live from the data files.
import { Link as RouterLink } from "react-router-dom";
import { Box, Flex, Grid, Link, Text } from "@chakra-ui/react";
import { ACCENT, BODY, DIM, FG, GRID, HEAT, INSET, MONO, MUTED, PANEL, SERIF, alpha } from "../theme";
import { LiveDot, Mono } from "./ui";
import { SportIcon, TrackSvg, fmtDate, sportColor, useSynced } from "../fitness/ui";
import { useCardTrack } from "../fitness/ActivityCard";
import { zoneFor } from "../fitness/model";
import { buildByDate, buildStreaks } from "../fitness/streaks";

const Row = ({ label, children, to }) => {
  const inner = (
    <Grid templateColumns="92px 1fr" gap={3} px={5} py={3} borderTop={`1px solid ${GRID}`} alignItems="baseline"
      _hover={to ? { bg: alpha("#ffffff", 0.02) } : undefined} transition="background .2s">
      <Mono fontSize="9px">{label}</Mono>
      <Box minW={0}>{children}</Box>
    </Grid>
  );
  return to ? (
    <Link as={RouterLink} to={to} display="block" _hover={{ textDecoration: "none" }}>
      {inner}
    </Link>
  ) : (
    inner
  );
};

const NowCard = ({ fitness, tennis, journal }) => {
  const recent = (fitness && fitness.recent && fitness.recent[0]) || null;
  const full = recent && fitness.activities ? fitness.activities.find((a) => a.id === recent.id) : null;
  const act = recent ? { ...full, ...recent } : null;
  const track = useCardTrack(act || {});
  const synced = useSynced(fitness);
  if (!fitness) return null;

  const last = fitness.fitness && fitness.fitness[fitness.fitness.length - 1];
  const zone = last && zoneFor(last.tsb);
  const streak = fitness.activities ? buildStreaks(buildByDate(fitness.activities), fitness.generated) : null;
  const note = journal && journal[0];
  const t = tennis && tennis.stats;
  const color = act ? sportColor(act.group) : ACCENT;

  return (
    <Box bg={alpha(PANEL, 0.88)} backdropFilter="blur(10px)" border={`1px solid ${GRID}`} borderRadius="2xl" overflow="hidden"
      boxShadow="0 30px 80px rgba(0,0,0,0.45)">
      <Flex justify="space-between" align="center" px={5} py={3.5}>
        <Flex align="center" gap={2.5}>
          <LiveDot />
          <Mono color={FG}>Right now</Mono>
        </Flex>
        <Mono>Synced {synced}</Mono>
      </Flex>

      {act && (
        <Link as={RouterLink} to={`/fitness/a/${act.id}`} display="block" borderTop={`1px solid ${GRID}`}
          _hover={{ textDecoration: "none", bg: alpha("#ffffff", 0.02) }}>
          <Flex gap={4} px={5} py={4} align="center">
            <Box w="76px" h="76px" flexShrink={0} bg={INSET} borderRadius="xl" p={2} border={`1px solid ${GRID}`}>
              {track ? (
                <TrackSvg track={track} stroke={color} strokeWidth={0.05} glow />
              ) : (
                <Flex w="100%" h="100%" align="center" justify="center">
                  <SportIcon group={act.group} size={30} />
                </Flex>
              )}
            </Box>
            <Box minW={0}>
              <Mono color={color} fontSize="9px" mb={1}>
                Last session · {fmtDate(act.date)}
              </Mono>
              <Text color={FG} fontWeight={600} lineHeight="1.3" noOfLines={2} letterSpacing="-0.01em">
                {act.name}
              </Text>
              <Text fontFamily={MONO} fontSize="xs" color={MUTED} mt={1}>
                {act.dur}
                {act.km >= 1 ? ` · ${act.km} km` : ""}
                {act.avgHR ? ` · ${Math.round(act.avgHR)} bpm` : ""}
              </Text>
            </Box>
          </Flex>
        </Link>
      )}

      <Row label="Day job">
        <Text color={FG} fontSize="sm">
          Drones at <Text as="span" color={ACCENT}>Vantage Robotics</Text>
        </Text>
      </Row>
      {last && (
        <Row label="Training" to="/fitness/engine">
          <Text color={FG} fontSize="sm">
            {streak && streak.current > 1 ? `${streak.current}-week streak · ` : ""}
            <Text as="span" color={zone.color}>{zone.name.toLowerCase()}</Text>
            <Text as="span" color={MUTED} fontFamily={MONO} fontSize="xs">
              {" "}
              (form {last.tsb > 0 ? "+" : ""}
              {Math.round(last.tsb)})
            </Text>
          </Text>
        </Row>
      )}
      {t && (
        <Row label="Tennis" to="/fitness/tennis">
          <Text color={FG} fontSize="sm">
            <Text as="span" fontFamily={MONO} color={ACCENT}>{t.wins}</Text>
            <Text as="span" color={MUTED}>–</Text>
            <Text as="span" fontFamily={MONO} color={HEAT}>{t.losses}</Text> career
            {tennis.nemesis ? (
              <Text as="span" color={MUTED}> · nemesis: <Text as="span" color={FG}>{tennis.nemesis}</Text></Text>
            ) : null}
          </Text>
        </Row>
      )}
      {fitness.cards && fitness.cards.sleep && (
        <Row label="Sleeping" to="/fitness/health">
          <Text color={FG} fontSize="sm">
            {fitness.cards.sleep} h a night
            {fitness.cards.rhr ? <Text as="span" color={MUTED}> · resting HR {fitness.cards.rhr}</Text> : null}
          </Text>
        </Row>
      )}
      {note && (
        <Row label="Thinking" to={`/journal/${note.id}`}>
          <Text color={BODY} fontFamily={SERIF} fontSize="lg" lineHeight="1.25" noOfLines={2}>
            “{note.text || note.title || note.caption}”
          </Text>
          <Text fontFamily={MONO} fontSize="10px" color={DIM} mt={1}>
            {note.dateLabel} · from the journal →
          </Text>
        </Row>
      )}
    </Box>
  );
};

export default NowCard;
